import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { create } from 'zustand';
import { Button, Wordmark } from './components';

/**
 * App-wide confirmation modal. Replaces native `window.confirm()` everywhere
 * with a branded, accessible dialog that honors the brand color palette and
 * typography.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Generate your invention strategy memo?',
 *     body: 'Your advisor will run a real prior-art search...',
 *     confirmLabel: 'Generate memo',
 *     destructive: false,
 *   });
 *   if (!ok) return;
 *
 * Mount <ConfirmHost /> once at the app root.
 */

export interface ConfirmOptions {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

interface PendingConfirm extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

interface ConfirmState {
  pending: PendingConfirm | null;
  ask: (opts: ConfirmOptions) => Promise<boolean>;
  resolve: (ok: boolean) => void;
}

const useConfirmStore = create<ConfirmState>((set, get) => ({
  pending: null,
  ask: (opts) =>
    new Promise<boolean>((resolve) => {
      set({ pending: { ...opts, resolve } });
    }),
  resolve: (ok) => {
    const cur = get().pending;
    if (cur) {
      cur.resolve(ok);
      set({ pending: null });
    }
  },
}));

/**
 * Hook returning an `async confirm()` function. Resolves to true if the user
 * accepts, false on cancel or dismiss.
 */
export function useConfirm() {
  const ask = useConfirmStore((s) => s.ask);
  return useCallback((opts: ConfirmOptions) => ask(opts), [ask]);
}

export function ConfirmHost() {
  const pending = useConfirmStore((s) => s.pending);
  const resolve = useConfirmStore((s) => s.resolve);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (pending) {
      // Trigger fade-in on next paint
      setMounted(false);
      const t = window.setTimeout(() => setMounted(true), 10);
      return () => window.clearTimeout(t);
    }
    setMounted(false);
  }, [pending]);

  // ESC to dismiss
  useEffect(() => {
    if (!pending) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') resolve(false);
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) resolve(true);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [pending, resolve]);

  if (!pending) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      style={{
        position: 'fixed',
        inset: 0,
        // Above the app's bottom nav and mobile sign-up bar.
        zIndex: 2000,
        background: 'rgba(15, 23, 20, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'max(16px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom))',
        opacity: mounted ? 1 : 0,
        transition: 'opacity 160ms ease',
      }}
      onMouseDown={(e) => {
        // Click on backdrop dismisses
        if (e.target === e.currentTarget) resolve(false);
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 480,
          // Never taller than the screen: the body scrolls and the buttons stay
          // in view (long overlap explanations, large phone text sizes).
          maxHeight: 'calc(100dvh - 32px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: 'var(--surface-page)',
          borderRadius: 16,
          padding: 'clamp(20px, 5vw, 32px) clamp(20px, 5vw, 32px) clamp(18px, 4.5vw, 28px)',
          boxShadow:
            '0 1px 2px rgba(15,23,20,0.06), 0 32px 80px rgba(15,23,20,0.35)',
          transform: mounted ? 'none' : 'translateY(8px)',
          transition: 'transform 200ms cubic-bezier(.2,.7,.3,1.1)',
          border: '1px solid var(--border-hairline)',
        }}
      >
        <div style={{ marginBottom: 18, opacity: 0.55, flexShrink: 0 }}>
          <Wordmark size={14} />
        </div>
        <h2
          id="confirm-title"
          className="f-display"
          style={{
            fontSize: 24,
            lineHeight: 1.25,
            marginBottom: pending.body ? 14 : 28,
            fontWeight: 400,
            flexShrink: 0,
          }}
        >
          {pending.title}
        </h2>
        {pending.body && (
          <div
            style={{
              fontSize: 15,
              lineHeight: 1.6,
              color: 'var(--text-primary)',
              opacity: 0.85,
              marginBottom: 20,
              minHeight: 0,
              overflowY: 'auto',
              overscrollBehavior: 'contain',
            }}
          >
            {pending.body}
          </div>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            flexWrap: 'wrap',
            flexShrink: 0,
            paddingTop: 8,
          }}
        >
          <Button variant="secondary" onClick={() => resolve(false)}>
            {pending.cancelLabel ?? 'Cancel'}
          </Button>
          <Button
            variant={pending.destructive ? 'danger' : 'primary'}
            onClick={() => resolve(true)}
          >
            {pending.confirmLabel ?? 'Confirm'}
          </Button>
        </div>
      </div>
    </div>
  );
}
